import type { DocumentData, DocumentSnapshot } from "firebase/firestore";
import { dateValue } from "@/models/firestore";
import {
  APP_NAVIGATION_DESTINATION_IDS,
  appDestinationScope,
  type SBotAppDestinationID,
  type SBotAppRoute,
} from "@/features/ai/services/appNavigation";

export const USER_NOTIFICATION_KINDS = [
  "espnAccessRequestSubmitted",
  "espnAccessRequestDecided",
  "supportRequestCreated",
  "supportRequestReply",
  "supportAccessRequested",
  "supportAccessGrantDecided",
  "supportAccessGrantEnded",
  "leagueBillingInvoice",
] as const;

export type KnownUserNotificationKind = (typeof USER_NOTIFICATION_KINDS)[number];

export type UserNotificationKind =
  | { type: "known"; value: KnownUserNotificationKind }
  | { type: "unknown"; value: string };

export interface UserNotification {
  id: string;
  userID: string;
  kindRaw: string;
  kind: UserNotificationKind;
  title: string;
  body: string;
  destinationID: string;
  leagueID: string | null;
  resourceID: string | null;
  createdAt: Date;
  readAt: Date | null;
}

const KNOWN_KINDS = new Set<string>(USER_NOTIFICATION_KINDS);
const KNOWN_DESTINATIONS = new Set<string>(APP_NAVIGATION_DESTINATION_IDS);

function requiredString(data: DocumentData, field: string): string | null {
  return typeof data[field] === "string" ? data[field] : null;
}

function optionalString(data: DocumentData, field: string): string | null {
  return typeof data[field] === "string" ? data[field] : null;
}

export function userNotificationKind(rawValue: string): UserNotificationKind {
  return KNOWN_KINDS.has(rawValue)
    ? { type: "known", value: rawValue as KnownUserNotificationKind }
    : { type: "unknown", value: rawValue };
}

/**
 * Decodes a server-written `userNotifications` document. Unknown kinds and
 * destinations remain displayable; malformed required fields reject the
 * document so one bad server record cannot break the notification center.
 */
export function decodeUserNotification(
  id: string,
  data: DocumentData,
): UserNotification | null {
  const userID = requiredString(data, "userID");
  const kindRaw = requiredString(data, "kind");
  const title = requiredString(data, "title");
  const body = requiredString(data, "body");
  const createdAt = dateValue(data.createdAt);

  if (!id || userID === null || kindRaw === null || title === null || body === null || !createdAt) {
    return null;
  }

  return {
    id,
    userID,
    kindRaw,
    kind: userNotificationKind(kindRaw),
    title,
    body,
    destinationID: optionalString(data, "destinationID") ?? "",
    leagueID: optionalString(data, "leagueID"),
    resourceID: optionalString(data, "resourceID"),
    createdAt,
    readAt: dateValue(data.readAt) ?? null,
  };
}

export function decodeUserNotificationSnapshot(
  snapshot: DocumentSnapshot<DocumentData>,
): UserNotification | null {
  return snapshot.exists() ? decodeUserNotification(snapshot.id, snapshot.data()) : null;
}

export function isUnreadNotification(notification: UserNotification): boolean {
  return notification.readAt === null;
}

/**
 * Shapes onward navigation to the destination's declared arity. A resource ID
 * may also be inline-action context, so it is intentionally dropped for
 * global and workspace destinations.
 */
export function userNotificationRoute(notification: UserNotification): SBotAppRoute | null {
  // Functions and iOS use this semantic destination name for Labs-admin
  // notices; the web navigation catalog calls the same surface `admin`.
  if (notification.destinationID === "sBotAdminHome") {
    return { destination: "admin" };
  }
  if (!KNOWN_DESTINATIONS.has(notification.destinationID)) return null;

  const destination = notification.destinationID as SBotAppDestinationID;
  const scope = appDestinationScope(destination);

  if (scope === "entity") {
    return notification.leagueID && notification.resourceID
      ? { destination, leagueID: notification.leagueID, resourceID: notification.resourceID }
      : null;
  }
  if (scope === "resource") {
    return notification.resourceID
      ? { destination, resourceID: notification.resourceID }
      : null;
  }
  if (scope === "workspace") {
    return notification.leagueID
      ? { destination, leagueID: notification.leagueID }
      : null;
  }
  return { destination };
}

export function partitionUserNotifications(notifications: readonly UserNotification[]): {
  unread: UserNotification[];
  earlier: UserNotification[];
} {
  return {
    unread: notifications.filter(isUnreadNotification),
    earlier: notifications.filter((notification) => !isUnreadNotification(notification)),
  };
}

export function unreadBadgeText(count: number): string | null {
  if (count <= 0) return null;
  return count > 99 ? "99+" : String(count);
}
